import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useConfig } from '../config/ConfigContext';
import apiClient from '../lib/apiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SEOHead from '../components/SEOHead';
import { trackEvent, trackAddToCart } from '../components/Analytics';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [loading, setLoading] = useState(true);
  const { dispatch } = useCart();
  const navigate = useNavigate();
  const { config } = useConfig();

  // Helper function to get price for selected size
  const getPriceForSize = (product, size) => {
    if (!product?.details?.pricing_variants || !size) {
      return product?.price || 0;
    }
    // Case-insensitive and whitespace-insensitive matching
    const normalizedSize = size.toLowerCase().replace(/\s/g, '');
    const variant = product.details.pricing_variants.find(v =>
      v.size.toLowerCase().replace(/\s/g, '') === normalizedSize
    );

    if (variant) {
      return variant.price;
    }
    return product.price;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await apiClient.getProduct(id);

        setProduct(response);
        setCurrentImageIndex(response.thumbnail_index || 0);
        if (response.sizes.length > 0) {
          const firstSize = response.sizes[0];
          setSelectedSize(firstSize);
          const calculatedPrice = getPriceForSize(response, firstSize);
          setCurrentPrice(calculatedPrice);
        } else {
          setCurrentPrice(response.price);
        }
        trackEvent('view_item', {
          currency: 'USD',
          value: response.price,
          items: [{
            item_id: response.id,
            item_name: response.name,
            category: response.category,
            quantity: 1,
            price: response.price
          }]
        });
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const addToCart = () => {
    if (!product.in_stock) return;

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        product_id: product.id,
        quantity: quantity,
        size: selectedSize || null
      }
    });

    // Track add to cart event
    trackAddToCart(product.id, product.name, currentPrice * quantity);

    // Enhanced success feedback
    const notification = document.createElement('div');
    notification.className = 'fixed top-24 right-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 fade-in-up';
    notification.innerHTML = '✅ Added to cart!';
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.remove();
    }, 3000);
  };

  if (loading || !config) {
    return <LoadingSpinner message="Loading product details..." />;
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEOHead title="Product Not Found" />
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Link to="/products" className="inline-block bg-brand-blue hover:bg-brand-red text-white font-semibold px-6 py-3 rounded-lg transition-colors">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-4 sm:pt-8 md:pt-24 pb-32 md:pb-12">
      <SEOHead
        title={product.name}
        description={product.description}
        image={product.images[product.thumbnail_index || 0]}
        type="product"
        canonical={`${window.location.origin}/product/${product.id}`}
        additionalMeta={[
          { property: 'product:price:amount', content: product.price },
          { property: 'product:price:currency', content: 'USD' },
          { property: 'product:availability', content: product.in_stock ? 'in stock' : 'out of stock' }
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Enhanced Image Gallery */}
          <div className="fade-in-up">
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <img
                src={product.images[currentImageIndex] || '/api/placeholder/600/600'}
                alt={product.name}
                loading="lazy"
                className="w-full h-96 lg:h-[500px] object-cover transition-all duration-500"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-4 overflow-x-auto pb-6 px-2 pt-2 snap-x snap-mandatory lg:grid lg:grid-cols-4 lg:overflow-visible lg:px-0 mt-2 mb-8">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 bg-white rounded-lg shadow-md overflow-visible transition-all duration-300 snap-start ${currentImageIndex === index ? 'ring-4 ring-brand-blue' : 'hover:ring-2 hover:ring-blue-200'
                      }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-20 object-cover rounded-lg"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Enhanced Product Info */}
          <div className="slide-in-right">
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl font-display font-bold text-gray-900 mb-4">{product.name}</h1>
                <p className="text-3xl font-bold text-gray-900 mb-6">
                  {currentPrice > 1 ? `$${currentPrice.toFixed(2)}` : null}
                </p>
                <p className="text-gray-600 text-lg leading-relaxed">{product.description}</p>
              </div>

              {/* Enhanced Size Selection */}
              {product.sizes.length > 0 && (
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    {product.category === 'sofa_beds' ? 'Select Color' : 'Size'}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => {
                          setSelectedSize(size);
                          const newPrice = getPriceForSize(product, size);
                          setCurrentPrice(newPrice);
                        }}
                        className={`px-3 py-3 border-2 rounded-lg font-medium text-sm transition-all duration-300 ${selectedSize === size
                          ? 'border-brand-blue bg-blue-50 text-brand-red shadow-lg'
                          : 'border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Specifications Section */}
              {product.details && (
                Object.keys(product.details).some(key => ['warranty', 'firmness', 'height', 'specifications', 'available_colors', 'dimensions'].includes(key))
              ) && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Specifications</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {/* Common Fields */}
                      {product.details.warranty && (
                        <div>
                          <span className="text-gray-600">Warranty:</span>
                          <span className="ml-2 font-medium text-gray-900">{product.details.warranty}</span>
                        </div>
                      )}
                      {product.details.firmness && (
                        <div>
                          <span className="text-gray-600">Firmness:</span>
                          <span className="ml-2 font-medium text-gray-900">{product.details.firmness}</span>
                        </div>
                      )}
                      {product.details.height && (
                        <div>
                          <span className="text-gray-600">Height / Thickness:</span>
                          <span className="ml-2 font-medium text-gray-900">{product.details.height}</span>
                        </div>
                      )}
                      {product.details.available_colors && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Available Colors:</span>
                          <span className="ml-2 font-medium text-gray-900">
                            {Array.isArray(product.details.available_colors)
                              ? product.details.available_colors.join(', ')
                              : product.details.available_colors}
                          </span>
                        </div>
                      )}
                      {product.details.dimensions && (
                        <div className="col-span-2">
                          <span className="text-gray-600">Dimensions:</span>
                          <span className="ml-2 font-medium text-gray-900">{product.details.dimensions}</span>
                        </div>
                      )}
                      {/* Note/General Specifications (Rendered last as a block) */}
                      {product.details.specifications && (
                        <div className="col-span-2 mt-2 pt-2 border-t border-gray-200">
                          <span className="text-gray-600 block mb-1 underline decoration-dotted">Note & Specifications:</span>
                          <p className="text-gray-900 font-medium leading-relaxed italic">{product.details.specifications}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Enhanced Quantity Selection */}
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-3">Quantity</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold px-4 py-2 rounded-lg transition-colors"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 text-center px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  />
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold px-4 py-2 rounded-lg transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Enhanced Add to Cart Button - Hidden on Mobile (fixed bar instead) */}
              <button
                onClick={addToCart}
                disabled={!product.in_stock}
                className={`hidden md:flex w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 items-center justify-center ${product.in_stock
                  ? 'bg-brand-blue hover:bg-brand-red text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {product.in_stock ? '🛒 Add to Cart' : 'Out of Stock'}
              </button>

              {/* Enhanced Product Details */}
              <div className="bg-gray-100 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Product Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-600">Category:</span>
                    <span className="text-gray-900 capitalize">{product.category.replace('_', ' ')}</span>
                  </div>
                  {product.subcategory && (
                    <div className="flex justify-between">
                      <span className="font-semibold text-gray-600">Type:</span>
                      <span className="text-gray-900 capitalize">{product.subcategory.replace('_', ' ')}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-600">Availability:</span>
                    <span className={`font-semibold ${product.in_stock ? 'text-green-600' : 'text-red-600'}`}>
                      {product.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Add to Cart Bar - Mobile Only */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-300 p-4 shadow-2xl md:hidden z-[60] safe-area-inset-bottom">
          <div className="flex items-center gap-3 max-w-7xl mx-auto px-2">
            <div className="flex-shrink-0">
              <div className="text-xs text-gray-500">Total</div>
              <div className="text-xl font-bold text-gray-900 whitespace-nowrap">
                {currentPrice > 1 ? `$${(currentPrice * quantity).toFixed(2)}` : null}
              </div>
            </div>
            <button
              onClick={addToCart}
              disabled={!product.in_stock}
              className={`flex-1 py-4 px-6 rounded-xl font-bold text-base transition-all duration-300 min-h-[56px] flex items-center justify-center ${product.in_stock
                ? 'bg-brand-blue active:bg-brand-red text-white shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
            >
              {product.in_stock ? '🛒 Add to Cart' : 'Out of Stock'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
